import { normalizeError } from '@brew/observability/core'
import {
  type Attributes,
  type Span,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api'
import pRetry from 'p-retry'
import { getConvexAppError } from '@/lib/errors/app-error'
import { isObjectLike } from '@/lib/shared/guards/is-object-like'

const DEFAULT_ATTEMPTS = 3
const MIN_TIMEOUT_MS = 500
const MAX_TIMEOUT_MS = 5000
const MAX_PROVIDER_RETRY_DELAY_MS = 60_000
const tracer = trace.getTracer('brew.external-calls')
const PROVIDER_HINTS = [
  'anthropic',
  'apollo',
  'braze',
  'brevo',
  'browser-use',
  'clerk',
  'cohere',
  'convex',
  'customerio',
  'entri',
  'fal',
  'figma',
  'firecrawl',
  'gemini',
  'giphy',
  'google',
  'hubspot',
  'iterable',
  'klaviyo',
  'mailchimp',
  'mailjet',
  'mailgun',
  'mongodb',
  'openai',
  'onesignal',
  'postmark',
  'revenuecat',
  'resend',
  'sendgrid',
  'shopify',
  'stytch',
  'stripe',
  'supabase',
  'together',
  'turbopuffer',
  'twilio',
  'unsplash',
  'vercel',
  'workos',
] as const

type RetryPredicate = (error: unknown) => boolean

export type ExternalRetryOptions = {
  label?: string
  attempts?: number
  shouldRetry?: RetryPredicate
  signal?: AbortSignal
  /** Declares whether replaying the request can duplicate a side effect. */
  operation?: 'safe_read' | 'idempotent_write' | 'non_idempotent_write'
  /** Per-attempt deadline for raw fetch calls. */
  timeoutMs?: number
  /**
   * Retry when OUR OWN per-attempt `timeoutMs` deadline fires (a `TimeoutError`
   * from `AbortSignal.timeout`). Off by default because a deadline usually means
   * the work is genuinely too slow to be worth repeating — but for a slow,
   * idempotent upstream (e.g. Figma's image renderer, which routinely stalls
   * past a fixed deadline and then succeeds) one more attempt is the difference
   * between a usable response and a spurious hard failure.
   *
   * A caller-initiated abort (via `options.signal`) is an `AbortError` and is
   * NEVER retried, regardless of this flag.
   */
  retryOnTimeout?: boolean
  /** Stable provider slug for trace filtering (for example `stripe`). */
  provider?: string
  /**
   * A provider header that paces a 429 retry when `Retry-After` is absent
   * (Mailgun documents `X-RateLimit-Reset` for this). Its value may be the
   * seconds to wait or the reset time as an epoch in seconds or milliseconds.
   */
  retryDelayHeader?: string
  /** Stable low-cardinality operation name; never include an id or URL. */
  operationName?: string
}

class RetryableResponseError extends Error {
  readonly response: Response
  readonly retryAfterMs?: number
  readonly status: number

  constructor(response: Response, label: string, retryAfterMs?: number) {
    super(`${label} failed with HTTP ${response.status}`)
    this.name = 'RetryableResponseError'
    this.response = response
    this.retryAfterMs = retryAfterMs
    this.status = response.status
  }
}

/** A reset header's delay: seconds to wait, or an epoch in s or ms. */
function parseResetHeaderDelayMs(value: string): number | undefined {
  const reset = Number(value)
  if (!Number.isFinite(reset) || reset < 0) {
    return
  }
  let delayMs = reset * 1000
  if (reset > 1e12) {
    delayMs = reset - Date.now()
  } else if (reset > 1e9) {
    delayMs = reset * 1000 - Date.now()
  }
  return Math.min(MAX_PROVIDER_RETRY_DELAY_MS, Math.max(0, Math.ceil(delayMs)))
}

function parseProviderRetryDelayMs(
  response: Response,
  retryDelayHeader?: string
): number | undefined {
  const rawValue =
    response.headers.get('retry-after') ??
    response.headers.get('x-sib-ratelimit-reset')
  const value = rawValue?.trim()
  if (!value) {
    const reset = retryDelayHeader
      ? response.headers.get(retryDelayHeader)?.trim()
      : undefined
    return reset ? parseResetHeaderDelayMs(reset) : undefined
  }

  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(
      MAX_PROVIDER_RETRY_DELAY_MS,
      Math.max(0, Math.ceil(seconds * 1000))
    )
  }

  const timestamp = Date.parse(value)
  if (!Number.isFinite(timestamp)) {
    return
  }

  return Math.min(
    MAX_PROVIDER_RETRY_DELAY_MS,
    Math.max(0, timestamp - Date.now())
  )
}

function waitForRetryDelay(
  delayMs: number,
  signal: AbortSignal | undefined
): Promise<void> {
  if (delayMs <= 0) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId)
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
    }
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, delayMs)

    if (signal?.aborted) {
      onAbort()
      return
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function getStatusCode(error: unknown): number | undefined {
  if (error instanceof RetryableResponseError) {
    return error.status
  }

  if (!isObjectLike(error)) {
    return
  }

  const status = error.status
  if (typeof status === 'number') {
    return status
  }

  const statusCode = error.statusCode
  if (typeof statusCode === 'number') {
    return statusCode
  }
}

function isAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  return (
    error.name === 'AbortError' ||
    error.name === 'TimeoutError' ||
    error.message.toLowerCase().includes('aborted')
  )
}

/**
 * 4xx responses are by definition client errors — retrying with the same
 * payload won't change the outcome. The only common-sense exceptions are
 * 408 (Request Timeout) and 429 (Too Many Requests), both of which a retry
 * with backoff genuinely helps.
 *
 * 5xx responses are server-side and usually transient, so we retry all of
 * them by default.
 */
function isRetryableStatus(status: number): boolean {
  if (status >= 500) {
    return true
  }
  if (status === 408 || status === 429) {
    return true
  }
  // All other 4xx (400, 401, 403, 404, 405, 409, 410, 422, 423, …) are
  // permanent client errors. Retrying spams the API and doubles latency.
  if (status >= 400 && status < 500) {
    return false
  }
  // 3xx is a valid terminal answer, not a failure. Callers normally never
  // see one (fetch auto-follows), but the SSRF redirect-guarded wrapper
  // (`createRedirectGuardedFetch`) uses `redirect: 'manual'` and inspects
  // each hop itself — retrying a redirect response would just burn
  // attempts + backoff on every hop.
  if (status >= 300 && status < 400) {
    return false
  }
  return true
}

/**
 * Default retry predicate. Exported so call sites that want to layer
 * additional fast-fail checks (e.g. a config-error short-circuit) can
 * compose with the same abort / 4xx / non-retryable behavior used when
 * no custom `shouldRetry` is supplied.
 *
 * Returns `false` for: abort errors, retryable=false app errors, and
 * non-retryable HTTP status codes (400, 401, 403, 404, 422, …).
 */
export function shouldRetryByDefault(error: unknown): boolean {
  if (isAbortError(error)) {
    return false
  }

  const appError = getConvexAppError(error)
  if (appError?.retryable === false) {
    return false
  }

  const status = getStatusCode(error)
  if (typeof status === 'number') {
    return isRetryableStatus(status)
  }

  return true
}

function resolveAttempts(attempts?: number): number {
  if (typeof attempts !== 'number' || Number.isNaN(attempts)) {
    return DEFAULT_ATTEMPTS
  }

  return Math.max(1, Math.floor(attempts))
}

function inferProvider(label: string | undefined): string | undefined {
  const normalized = label?.toLowerCase().replaceAll('_', '-')
  return PROVIDER_HINTS.find((provider) => normalized?.includes(provider))
}

function stableOperationName(
  candidate: string | undefined,
  fallback: string
): string {
  const normalized = candidate?.trim()
  return normalized && /^[a-zA-Z][a-zA-Z0-9_.:-]{1,79}$/.test(normalized)
    ? normalized
    : fallback
}

function providerForFetch(input: Parameters<typeof fetch>[0]): string {
  try {
    const rawUrl =
      typeof input === 'string' || input instanceof URL
        ? String(input)
        : input.url
    const hostname = new URL(rawUrl).hostname.toLowerCase()
    return (
      PROVIDER_HINTS.find((provider) =>
        [provider, provider.replaceAll('-', '')].some((hint) =>
          hostname.includes(hint)
        )
      ) ?? 'external'
    )
  } catch {
    return 'external'
  }
}

/**
 * `AbortSignal.timeout` rejects with a `TimeoutError`; a caller-initiated abort
 * rejects with an `AbortError`. Only the former is ours to retry.
 */
function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'TimeoutError'
}

function shouldRetryError(
  error: unknown,
  options: Pick<ExternalRetryOptions, 'shouldRetry' | 'retryOnTimeout'> = {}
): boolean {
  if (options.retryOnTimeout && isTimeoutError(error)) {
    return true
  }

  if (options.shouldRetry) {
    return options.shouldRetry(error)
  }

  return shouldRetryByDefault(error)
}

/**
 * The retry module is shared by server and browser callers, so it must not
 * import the `server-only` observability entry. Normalize the exception with
 * the runtime-neutral core before recording it on the explicit Convex span.
 */
function recordRetrySpanError(
  span: Span,
  error: unknown,
  attributes: Attributes
): void {
  if (!span.isRecording()) {
    return
  }
  const normalized = normalizeError(error)
  span.recordException({
    name: normalized.kind,
    message: normalized.message,
    stack: normalized.stack,
  })
  span.setAttributes({
    ...attributes,
    'error.type': normalized.kind,
    'error.message': normalized.message,
    ...(normalized.stack ? { 'error.stack': normalized.stack } : {}),
    ...(normalized.code ? { 'error.code': normalized.code } : {}),
  })
  span.setStatus({ code: SpanStatusCode.ERROR, message: normalized.message })
}

export async function withExternalRetry<T>(
  fn: () => Promise<T>,
  options: ExternalRetryOptions = {}
): Promise<T> {
  const attempts = resolveAttempts(options.attempts)
  const activeSpan = trace.getActiveSpan()
  const provider =
    options.provider ?? inferProvider(options.label) ?? 'external'
  const operationName = stableOperationName(
    options.operationName ?? options.label,
    'sdk_call'
  )
  let attemptNumber = 0

  activeSpan?.setAttributes({
    'brew.external.attempts.max': attempts,
    'brew.external.provider': provider,
    'brew.external.operation': operationName,
    ...(options.operation
      ? { 'brew.external.operation.kind': options.operation }
      : {}),
  })

  try {
    const result = await pRetry(
      () => {
        attemptNumber += 1
        return fn()
      },
      {
        retries: attempts - 1,
        factor: 2,
        minTimeout: MIN_TIMEOUT_MS,
        maxTimeout: MAX_TIMEOUT_MS,
        randomize: true,
        signal: options.signal,
        shouldRetry: ({ error }) => shouldRetryError(error, options),
        onFailedAttempt: async ({
          error,
          attemptNumber: failedAttempt,
          retriesLeft,
        }) => {
          if (!shouldRetryError(error, options)) {
            return
          }
          activeSpan?.addEvent('external.retry.attempt_failed', {
            'brew.external.attempt': failedAttempt,
            'brew.external.retries_left': retriesLeft,
            'brew.external.error.type':
              error instanceof Error ? error.name : typeof error,
            'brew.external.http.status_code': getStatusCode(error) ?? 0,
          })
          if (retriesLeft > 0 && error instanceof RetryableResponseError) {
            // A retryable 408/429/5xx still has a live body. Leaving it
            // open until GC holds the socket for every discarded attempt
            // (Greptile P2 on #1529). Cancel before the next hop; the last
            // exhausted response is returned to the caller intact.
            error.response.body?.cancel().catch(() => {
              // Already abandoning this attempt.
            })
            if (error.retryAfterMs !== undefined) {
              await waitForRetryDelay(error.retryAfterMs, options.signal)
            }
          }
        },
      }
    )
    activeSpan?.setAttributes({
      'brew.external.attempts.used': attemptNumber,
      'brew.external.result': 'success',
    })
    return result
  } catch (error) {
    activeSpan?.setAttributes({
      'brew.external.attempts.used': attemptNumber,
      'brew.external.result': 'failure',
      'brew.external.error.type':
        error instanceof Error ? error.name : typeof error,
    })
    activeSpan?.addEvent('external.retry.exhausted', {
      'brew.external.attempts.used': attemptNumber,
      'brew.external.http.status_code': getStatusCode(error) ?? 0,
    })
    throw error
  }
}

export async function fetchWithRetry(
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1],
  options: ExternalRetryOptions = {}
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const operation =
    options.operation ??
    (method === 'GET' || method === 'HEAD' || method === 'OPTIONS'
      ? 'safe_read'
      : 'non_idempotent_write')
  const retryOptions: ExternalRetryOptions = {
    ...options,
    operation,
    provider: options.provider ?? providerForFetch(input),
    operationName: options.operationName ?? `http.${method.toLowerCase()}`,
    attempts:
      operation === 'non_idempotent_write' && options.attempts === undefined
        ? 1
        : options.attempts,
  }

  try {
    return await withExternalRetry(async () => {
      const signals = [init?.signal, options.signal].filter(
        (signal): signal is AbortSignal =>
          signal !== null && signal !== undefined
      )
      if (options.timeoutMs !== 0) {
        signals.push(AbortSignal.timeout(options.timeoutMs ?? 15_000))
      }
      const requestInit = {
        ...init,
        ...(signals.length > 0
          ? {
              signal:
                signals.length === 1 ? signals[0] : AbortSignal.any(signals),
            }
          : {}),
      }
      const response = await fetch(input, requestInit)

      if (!response.ok && isRetryableStatus(response.status)) {
        throw new RetryableResponseError(
          response,
          options.label ?? 'external fetch',
          operation === 'safe_read' && response.status === 429
            ? parseProviderRetryDelayMs(response, options.retryDelayHeader)
            : undefined
        )
      }

      return response
    }, retryOptions)
  } catch (error) {
    if (error instanceof RetryableResponseError) {
      return error.response
    }

    throw error
  }
}

export async function retryConvexAction<T>(
  fn: () => Promise<T>,
  options: ExternalRetryOptions = {}
): Promise<T> {
  return tracer.startActiveSpan('convex.call', async (span) => {
    const operationName = stableOperationName(
      options.operationName ?? options.label,
      'convex.action'
    )
    span.setAttributes({
      'brew.convex.function': operationName,
      'brew.external.provider': 'convex',
    })
    try {
      return await withExternalRetry(fn, {
        ...options,
        provider: 'convex',
        label: options.label ?? 'convex action',
      })
    } catch (error) {
      recordRetrySpanError(span, error, {
        'brew.convex.function': operationName,
      })
      throw error
    } finally {
      span.end()
    }
  })
}
